import React from "react";
import MUIIconButton from "@mui/material/IconButton";
import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import TileDashboard from "../../../Tile/TileDashboard";
import MUIAddIcon from "@mui/icons-material/Add";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import EmptyState from "../../../EmptyState";
import DialogSearchDashboard from "../../../Dialog/Search/DialogSearchDashboard";
import Dashboard from "../../../../Utility/Crud/Dashboard";
import PropTypes from "prop-types";
import MUITextField from "@mui/material/TextField";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUISearchIcon from "@mui/icons-material/Search";
import CardClass from "../../../CardClass";
import MainContext from "../../../../Context/MainContext/MainContext";

export default class CardAdminTeamDashboard extends CardClass {
  static contextType = MainContext; // For legacy class components

  constructor(props) {
    super(props);

    this.state.dashboardDialogOpen = false;
    this.state.query = "";
    this.state.filteredTeamDashboards = this.props.teamDashboards;
  }

  /**
   * Set the search results on mount. This ensures that the list is sorted
   * appropriately on first render.
   */
  componentDidMount() {
    this.setState({
      filteredTeamDashboards: this.getFilteredTeamDashboards(this.state.query),
    });
  }

  /**
   * Handle removes or adds to the teamDashboards prop.
   */
  componentDidUpdate(prevProps) {
    if (prevProps.teamDashboards.length !== this.props.teamDashboards.length) {
      this.setState({
        filteredTeamDashboards: this.getFilteredTeamDashboards(
          this.state.query,
        ),
      });
    }
  }

  /**
   * Renders a list of dashboards on the team or an empty state if there are none.
   */
  renderContent() {
    const handleRemoveTeamDashboard = (removedTeamDashboard) => {
      this.props.onChange(
        this.props.teamDashboards.filter((existingTeamDashboard) => {
          return (
            existingTeamDashboard.dashboardId !==
            removedTeamDashboard.dashboardId
          );
        }),
      );
    };

    if (this.props.teamDashboards.length > 0) {
      if (this.state.filteredTeamDashboards.length > 0) {
        return (
          <>
            {this.renderSearchBox()}
            <MUIGrid container spacing={2}>
              {this.state.filteredTeamDashboards.map((teamDashboard) => {
                return (
                  <MUIGrid
                    key={teamDashboard.dashboardId}
                    item
                    xs={12}
                    sm={6}
                    md={4}
                  >
                    <TileDashboard
                      dashboard={
                        this.context.dashboardsIndexed[
                          teamDashboard.dashboardId
                        ]
                      }
                      iconButtons={[
                        <MUIIconButton
                          onClick={() =>
                            handleRemoveTeamDashboard(teamDashboard)
                          }
                        >
                          <MUIDeleteIcon />
                        </MUIIconButton>,
                      ]}
                    />
                  </MUIGrid>
                );
              })}
              {this.renderDialog()}
            </MUIGrid>
          </>
        );
      } else {
        return (
          <>
            {this.renderSearchBox()}
            <EmptyState
              line1="No results found"
              line2="Try searching for something a little different."
            />
            {this.renderDialog()}
          </>
        );
      }
    } else {
      return (
        <>
          <EmptyState
            line1="No Workflows"
            line2="Workflows added to this team will appear here"
          />
          {this.renderDialog()}
        </>
      );
    }
  }

  /**
   * Renders a search dialog of all dashboards
   */
  renderDialog() {
    const handleAddTeamDashboard = (dashboard) => {
      const duplicateTeamDashboards = this.props.teamDashboards.filter(
        (teamDashboard) => {
          return teamDashboard.dashboardId === dashboard.id;
        },
      );
      if (duplicateTeamDashboards.length === 0) {
        this.props.onChange(
          this.props.teamDashboards.concat([
            {
              teamId: this.props.team.id,
              dashboardId: dashboard.id,
            },
          ]),
        );
      }
    };

    return (
      <DialogSearchDashboard
        open={this.state.dashboardDialogOpen}
        onClose={() => this.setState({ dashboardDialogOpen: false })}
        onSelect={(dashboard) => {
          handleAddTeamDashboard(dashboard);
        }}
      />
    );
  }

  /**
   * Renders add dashboard button.
   */
  getActions() {
    const handleClick = () => {
      this.setState({
        dashboardDialogOpen: true,
      });
    };

    return [
      <MUIButton
        component="label"
        variant="outlined"
        startIcon={<MUIAddIcon />}
        onClick={handleClick}
      >
        Add Workflow
      </MUIButton>,
    ];
  }

  /**
   * Searches for team dashboards using the standard dashboard search.
   *
   * @param {string} query The search query.
   * @returns A list of filtered team dashboards.
   */
  getFilteredTeamDashboards(query) {
    const normalizedQuery = (query || "").trim();
    const dashboards = [];
    const missingTeamDashboards = [];
    this.props.teamDashboards.forEach((teamDashboard) => {
      const dashboard =
        this.context.dashboardsIndexed[teamDashboard.dashboardId];
      if (dashboard) {
        dashboards.push(dashboard);
      } else {
        missingTeamDashboards.push(teamDashboard);
      }
    });

    const filteredDashboards = Dashboard.search(
      dashboards,
      normalizedQuery,
      true,
    );

    const filteredTeamDashboards = [];
    filteredDashboards.forEach((dashboard) => {
      // Nested loop not ideal, but set size is small enough.
      filteredTeamDashboards.push(
        this.props.teamDashboards.find((teamDashboard) => {
          return teamDashboard.dashboardId === dashboard.id;
        }),
      );
    });

    // Keep missing/deleted dashboard links visible when not searching so
    // they can be removed from the team.
    if (!normalizedQuery) {
      filteredTeamDashboards.push(...missingTeamDashboards);
    }

    return filteredTeamDashboards;
  }

  /**
   * @returns {string} The title of the card.
   */
  getTitle() {
    return "Workflows";
  }

  /**
   * Render a search box.
   */
  renderSearchBox() {
    const handleChange = (e) => {
      this.setState({
        query: e.target.value,
        filteredTeamDashboards: this.getFilteredTeamDashboards(e.target.value),
      });
    };

    return (
      <MUITextField
        autoFocus={true}
        placeholder="Type to search..."
        hiddenLabel={true}
        fullWidth={true}
        variant="filled"
        size="small"
        onChange={handleChange}
        sx={{
          marginBottom: 2,
          "& .MuiInputLabel-root": { display: "none", height: 0 },
        }}
        InputProps={{
          startAdornment: (
            <MUIInputAdornment position="start">
              <MUISearchIcon />
            </MUIInputAdornment>
          ),
          disableUnderline: true,
          style: { borderRadius: 4 },
        }}
      />
    );
  }
}
